/* eslint-disable react/no-unknown-property */
/* eslint-disable no-underscore-dangle */
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import config from '../config';
import routes from '../routes';
import { findImageSources, getOptimalSource } from './media';

export const SITE_NAME = 'Spotlight';
export const DEFAULT_TITLE = 'SpotlightNews®';
export const DEFAULT_DESCRIPTION = [
  'Spotlight helps you discover, read, and follow news from trusted publishers,',
  'writers, and topics in one personalized experience.',
].join(' ');
export const DEFAULT_IMAGE_PATH = '/images/share-preview.png';

const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large';
const NOINDEX_ROBOTS = 'noindex,follow';

export const getSiteUrl = () => {
  const siteUrl = process.env.REACT_APP_PUBLIC_SITE_URL || config.appServer;

  if (siteUrl) {
    return siteUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  return 'https://my.spotlightnews.us';
};

export const absoluteUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const siteUrl = getSiteUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${siteUrl}${normalizedPath}`;
};

export const stripHtml = (value = '') => String(value)
  .replace(/<script[\s\S]*?<\/script>/gi, ' ')
  .replace(/<style[\s\S]*?<\/style>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'")
  .replace(/\s+/g, ' ')
  .trim();

export const truncate = (value = '', maxLength = 155) => {
  const cleanValue = stripHtml(value);

  if (cleanValue.length <= maxLength) {
    return cleanValue;
  }

  const clipped = cleanValue.slice(0, maxLength - 1);
  const lastSpace = clipped.lastIndexOf(' ');
  return `${clipped.slice(0, lastSpace > 80 ? lastSpace : clipped.length).trim()}…`;
};

export const getCurrentCanonicalUrl = () => {
  if (typeof window === 'undefined') {
    return absoluteUrl('/');
  }

  return absoluteUrl(window.location.pathname);
};

export const getDefaultImageUrl = () => absoluteUrl(DEFAULT_IMAGE_PATH);

const getOrigin = (url) => {
  if (!url) {
    return null;
  }

  try {
    return new URL(url, getSiteUrl()).origin;
  } catch (error) {
    return null;
  }
};

const buildPreconnectOrigins = (...urls) => Array.from(new Set([
  getOrigin(config.apiServer),
  getOrigin(config.apiEventServer),
  ...urls.map((url) => getOrigin(url)),
].filter(Boolean).filter((origin) => origin !== getOrigin(getSiteUrl()))));

export const getArticleImageUrl = (article = {}) => {
  const imageSources = findImageSources(article?.media);
  const imageSource = getOptimalSource(imageSources, 760)
    || article?.icon
    || article?.publisher?.default_image;

  return imageSource || getDefaultImageUrl();
};

export const getArticleCanonicalUrl = (article = {}) => {
  const slug = article?.slug;

  // Canonicalize to the server-rendered article on the news.* host so the SPA
  // page consolidates onto the same URL the SSR page self-canonicalizes to and
  // that lives in the sitemap. The slug must match byte-for-byte, so fall back
  // to the current URL only if the payload is missing it (the live API always
  // returns it).
  if (!slug) {
    return getCurrentCanonicalUrl();
  }

  return `${config.newsServer}${routes.news.article(slug)}`;
};

export const getPublisherCanonicalUrl = (publisher = {}) => {
  if (!publisher?.slug) {
    return getCurrentCanonicalUrl();
  }

  return absoluteUrl(routes.publishers.main(publisher.slug));
};

export const getWriterCanonicalUrl = (writer = {}) => {
  if (!writer?.slug) {
    return getCurrentCanonicalUrl();
  }

  return absoluteUrl(routes.writers.main(writer.slug));
};

export const compactObject = (data) => Object.entries(data).reduce((acc, [key, value]) => {
  if (value === undefined || value === null || value === '') {
    return acc;
  }

  if (Array.isArray(value) && value.length === 0) {
    return acc;
  }

  acc[key] = value;
  return acc;
}, {});

export const buildArticleJsonLd = (article = {}) => compactObject({
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  mainEntityOfPage: compactObject({
    '@type': 'WebPage',
    '@id': getArticleCanonicalUrl(article),
  }),
  headline: truncate(article?.tidy_title || article?.title, 110),
  description: truncate(article?.summary || article?.description || article?.body),
  image: [getArticleImageUrl(article)].filter(Boolean),
  datePublished: article?.published_at,
  dateModified: article?.updated_at || article?.published_at,
  author: (article?.writers || []).map((writer) => compactObject({
    '@type': 'Person',
    name: writer?.name || writer?.title,
    url: writer?.slug ? getWriterCanonicalUrl(writer) : undefined,
  })),
  publisher: compactObject({
    '@type': 'Organization',
    name: article?.publisher?.title || DEFAULT_TITLE,
    url: article?.publisher?.slug ? getPublisherCanonicalUrl(article.publisher) : getSiteUrl(),
    logo: compactObject({
      '@type': 'ImageObject',
      url: getDefaultImageUrl(),
    }),
  }),
});

const buildTitle = (title) => {
  if (!title) {
    return DEFAULT_TITLE;
  }

  return title.includes(SITE_NAME) ? title : `${title} | ${DEFAULT_TITLE}`;
};

const SeoHead = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
  type,
  noindex,
  robots,
  jsonLd,
  keywords,
  ogDescription,
  ogTitle,
  preloadImage,
  preloadImageUrl,
  preloadImageSrcSet,
  preloadImageSizes,
}) => {
  const metaTitle = buildTitle(title);
  const metaDescription = truncate(description || DEFAULT_DESCRIPTION);
  const resolvedCanonical = canonicalUrl || getCurrentCanonicalUrl();
  const resolvedImage = imageUrl || getDefaultImageUrl();
  const resolvedPreloadImage = preloadImageUrl || resolvedImage;
  const preconnectOrigins = buildPreconnectOrigins(resolvedImage, resolvedPreloadImage);
  const robotsContent = robots || (noindex ? NOINDEX_ROBOTS : DEFAULT_ROBOTS);
  const resolvedOgTitle = ogTitle || metaTitle;
  const resolvedOgDescription = ogDescription || metaDescription;

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={robotsContent} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={resolvedCanonical} />
      {preconnectOrigins.map((origin) => (
        <link key={`preconnect-${origin}`} rel="preconnect" href={origin} crossOrigin="" />
      ))}
      {preloadImage && resolvedPreloadImage && (
        <link
          rel="preload"
          as="image"
          href={resolvedPreloadImage}
          imagesrcset={preloadImageSrcSet}
          imagesizes={preloadImageSizes}
          fetchpriority="high"
        />
      )}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={resolvedImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

SeoHead.defaultProps = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  canonicalUrl: undefined,
  imageUrl: undefined,
  type: 'website',
  noindex: false,
  robots: undefined,
  jsonLd: undefined,
  keywords: undefined,
  ogDescription: undefined,
  ogTitle: undefined,
  preloadImage: false,
  preloadImageUrl: undefined,
  preloadImageSrcSet: undefined,
  preloadImageSizes: undefined,
};

SeoHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonicalUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  type: PropTypes.string,
  noindex: PropTypes.bool,
  robots: PropTypes.string,
  jsonLd: PropTypes.shape(),
  keywords: PropTypes.string,
  ogDescription: PropTypes.string,
  ogTitle: PropTypes.string,
  preloadImage: PropTypes.bool,
  preloadImageUrl: PropTypes.string,
  preloadImageSrcSet: PropTypes.string,
  preloadImageSizes: PropTypes.string,
};

export default SeoHead;
